
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Users, Activity, RefreshCw, Loader2, Building2, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface DashboardMetrics {
  messages_sent: number;
  active_campaigns: number;
  contacts_reached: number;
  response_rate: number;
}

const Index = () => {
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const { data: companyData, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', currentUser?.companyId],
    queryFn: async () => {
      if (!currentUser?.companyId) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', currentUser.companyId)
        .single();

      if (error) {
        console.error('Error fetching company:', error);
        throw error;
      }

      return data;
    },
    enabled: !!currentUser?.companyId
  });

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: async () => {
      const { data: businessMetrics, error } = await supabase
        .from('business_metrics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching metrics:', error);
        throw error;
      }

      return {
        messages_sent: businessMetrics?.[0]?.metric_value || 0,
        active_campaigns: 0,
        contacts_reached: 0,
        response_rate: 0
      } as DashboardMetrics;
    },
    refetchInterval: 30000
  });

  const handleUpdate = async () => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      }

      localStorage.clear();
      sessionStorage.clear();

      toast({
        title: "Recarregar Aplicação",
        description: "Limpeza de cache concluída. Recarregando a visualização...",
      });

      setTimeout(() => {
        window.location.href = window.location.href.split('#')[0];
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Erro durante a atualização:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao recarregar. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Dashboard</h2>
          <div className="flex items-center space-x-4 text-muted-foreground">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              <span>{currentUser?.name || 'Usuário'}</span>
            </div>
            {companyData && (
              <div className="flex items-center">
                <Building2 className="h-4 w-4 mr-2" />
                <span>{companyData.name}</span>
              </div>
            )}
          </div>
        </div>
        <Button 
          onClick={handleUpdate}
          variant="outline"
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Recarregar Visualização
        </Button>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Mensagens Enviadas
            </CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                metrics?.messages_sent.toLocaleString() || '0'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Campanhas Ativas
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                metrics?.active_campaigns.toLocaleString() || '0'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Contatos Alcançados
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                metrics?.contacts_reached.toLocaleString() || '0'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Taxa de Resposta
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                `${metrics?.response_rate.toFixed(1) || '0'}%`
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              className="w-full" 
              onClick={() => window.location.href = "/quick-messages"}
              disabled={isLoading}
            >
              Nova Mensagem Rápida
            </Button>
            <Button 
              className="w-full" 
              onClick={() => window.location.href = "/campaigns"}
              disabled={isLoading}
            >
              Nova Campanha
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
